import React, { useState, useEffect, useMemo } from 'react';
import { productService } from '../../services/productService';

const ProductSearch = ({
  onProductSelect,
  selectedProducts = [],
  searchQuery = '',
  onSearchQueryChange = null,
  selectedCategory = '',
  onlyAvailable = true,
  showFilters = true,
  locationId = null,
  premiumProducts = []
}) => {
  const [query, setQuery] = useState(searchQuery);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [internalSelectedCategory, setInternalSelectedCategory] = useState(selectedCategory);
  const [internalOnlyAvailable, setInternalOnlyAvailable] = useState(onlyAvailable);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Stany sortowania
  const [sortColumn, setSortColumn] = useState('nazwa');
  const [sortDirection, setSortDirection] = useState('asc');

  // Funkcja sortowania
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Komponent ikony sortowania
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Posortowane produkty
  const sortedProducts = useMemo(() => {
    if (!products || products.length === 0) return [];

    const sorted = [...products].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'nazwa':
          aVal = (a.name || a.nazwa || '').toLowerCase();
          bVal = (b.name || b.nazwa || '').toLowerCase();
          break;
        case 'stock':
          aVal = parseFloat(a.stock_quantity || a.stan_magazynowy || 0);
          bVal = parseFloat(b.stock_quantity || b.stan_magazynowy || 0);
          break;
        case 'price':
          aVal = parseFloat(a.price || a.cena || 0);
          bVal = parseFloat(b.price || b.cena || 0);
          break;
        default:
          aVal = '';
          bVal = '';
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal, 'pl');
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      if (sortDirection === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });

    // Obcinamy do 15 dopiero po posortowaniu calego pobranego zbioru
    return sorted.slice(0, 15);
  }, [products, sortColumn, sortDirection]);

  // Używaj zewnętrznych wartości jeśli są przekazane
  const currentCategory = showFilters ? internalSelectedCategory : selectedCategory;
  const currentOnlyAvailable = showFilters ? internalOnlyAvailable : onlyAvailable;

  // Załaduj kategorie przy inicjalizacji
  useEffect(() => {
    if (showFilters) {
      loadCategories();
    }
  }, [showFilters]);

  // Synchronizuj zewnętrzny searchQuery
  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  // Debounced search z AbortController dla unikniecia memory leak
  useEffect(() => {
    const controller = new AbortController();

    if (query.length < 2 && !currentCategory) {
      setProducts([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setError('');

      try {
        const response = await productService.getInventoryProducts({
          query: query,
          category: currentCategory,
          limit: 50,
          locationId: locationId,
          availableOnly: currentOnlyAvailable,
          signal: controller.signal
        });

        if (response.success) {
          let allProducts = response.products || [];

          // Skanowanie kodow kreskowych obsluguje hook useBarcodeScanner
          // Tu tylko wyswietlamy wyniki wyszukiwania.
          // Trzymamy pelny pobrany zbior - sortowanie i obciecie do 15
          // odbywa sie w useMemo (sortedProducts), zeby sortowac calosc.
          setProducts(allProducts);
          setShowResults(true);
        } else {
          setError(response.message || 'Brak produktów w magazynie');
          setProducts([]);
        }
      } catch (err) {
        // Ignoruj błędy AbortError - to normalne przy anulowaniu requestu
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        setError(err.message);
        setProducts([]);
      } finally {
        // Nie ustawiaj loading na false jeśli request został anulowany
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [query, currentCategory, currentOnlyAvailable, locationId]);

  const loadCategories = async () => {
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Błąd ładowania kategorii:', err);
    }
  };

  // searchProducts jest teraz zintegrowane w useEffect dla lepszego zarzadzania cleanup

  const handleProductSelect = (product) => {
    onProductSelect(product);
    setQuery('');
    if (onSearchQueryChange) {
      onSearchQueryChange('');
    }
    setShowResults(false);
  };

  const handleQueryChange = (newQuery) => {
    setQuery(newQuery);
    if (onSearchQueryChange) {
      onSearchQueryChange(newQuery);
    }
  };

  const handleBarcodeSearch = async (barcode) => {
    if (!barcode) return;

    setLoading(true);
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success) {
        handleProductSelect(response.data);
      } else {
        setError('Produkt o podanym kodzie kreskowym nie został znaleziony');
      }
    } catch (err) {
      setError('Błąd wyszukiwania po kodzie kreskowym');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Sprawdź czy to może być kod kreskowy (tylko cyfry, długość > 8)
      const isBarcodePattern = /^\d{8,}$/.test(query);
      if (isBarcodePattern) {
        handleBarcodeSearch(query);
      } else if (products.length > 0) {
        handleProductSelect(products[0]);
      }
    }
  };

  const isProductSelected = (productId) => {
    return selectedProducts.some(item => item.product_id === productId || item.id === productId);
  };

  return (
    <div style={{
      width: '100%',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '0.375rem',
      padding: '1rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
    }}>

      {/* Kontrolki wyszukiwania */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'end',
        flexWrap: 'nowrap',
        marginBottom: '1rem'
      }}>
        {/* Wyszukiwanie - pełna szerokość gdy brak filtrów */}
        <div style={{ flex: showFilters ? '0 0 45%' : '1', minWidth: '200px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Nazwa produktu lub kod kreskowy..."
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyPress={handleKeyPress}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '6px 10px',
                paddingLeft: '2rem',
                fontSize: '12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                boxSizing: 'border-box'
              }}
            />
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: '0.75rem'
            }}></i>
            {loading && (
              <div style={{
                position: 'absolute',
                right: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)'
              }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '0.75rem' }}></i>
              </div>
            )}
            {query && !loading && (
              <button
                type="button"
                data-pos-btn="true"
                onClick={() => {
                  handleQueryChange('');
                  setShowResults(false);
                }}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',

                  background: 'none',
                  color: 'var(--text-muted)',
                  fontSize: '0.75rem',
                  padding: 0
                }}
                title="Wyczyść wyszukiwanie"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Kategoria - 30% szerokości - tylko gdy showFilters=true */}
        {showFilters && (
          <div style={{ flex: '0 0 30%', minWidth: '110px' }}>
            <label style={{
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--text-muted)',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              <i className="fas fa-tags me-1" style={{ color: 'var(--warning)' }}></i>
              Kategoria
            </label>
            <select
              value={internalSelectedCategory}
              onChange={(e) => setInternalSelectedCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Wszystkie</option>
              {categories.map((category, index) => (
                <option key={index} value={category.name}>
                  {category.name} ({category.product_count || 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Checkbox - 25% szerokości - tylko gdy showFilters=true */}
        {showFilters && (
          <div style={{ flex: '0 0 25%', minWidth: '100px' }}>
            <label style={{
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--text-muted)',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              <i className="fas fa-eye me-1" style={{ color: 'var(--success)' }}></i>
              Widok
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 10px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              backgroundColor: 'var(--bg)',
              height: '33px' // Wyrównanie wysokości z selectem
            }}>
              <input
                type="checkbox"
                id="onlyAvailableInternal"
                checked={internalOnlyAvailable}
                onChange={(e) => setInternalOnlyAvailable(e.target.checked)}
                style={{ marginRight: '0.4rem' }}
              />
              <label htmlFor="onlyAvailableInternal" style={{
                margin: 0,
                cursor: 'pointer',
                fontSize: '11px',
                whiteSpace: 'nowrap'
              }}>
                <i className="fas fa-box me-1" style={{ color: 'var(--success)' }}></i>
                Dostępne
              </label>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--warning-light)',
          color: 'var(--warning)',
          border: '1px solid var(--warning-light)',
          borderRadius: '0.375rem',
          padding: '0.75rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.8rem'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ color: 'var(--warning)' }}></i>
          {error}
        </div>
      )}

      {showResults && products.length > 0 && (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          overflow: 'hidden',
          maxHeight: '350px',
          overflowY: 'auto',
          backgroundColor: 'var(--card-bg)'
        }}>
          <table style={{
            width: '100%',
            fontSize: '11px',
            borderCollapse: 'collapse'
          }}>
            <thead style={{
              backgroundColor: 'var(--bg)',
              borderBottom: '1px solid var(--border)',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <tr>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  userSelect: 'none'
                }} onClick={() => handleSort('nazwa')}>
                  Produkt<SortIcon column="nazwa" />
                </th>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  width: '80px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }} onClick={() => handleSort('stock')}>
                  Stan<SortIcon column="stock" />
                </th>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  width: '80px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }} onClick={() => handleSort('price')}>
                  Cena<SortIcon column="price" />
                </th>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  width: '80px'
                }}>
                  Akcja
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map((product) => {
                const stock = parseFloat(product.stock_quantity || product.stan_magazynowy) || 0;
                const price = parseFloat(product.price || product.cena) || 0;
                const isSelected = isProductSelected(product.id);
                const isService = product.typ_produktu === 'usluga';
                // Usługi nie mają stanu magazynowego - zawsze dostępne
                // Blokuj produkty z zerowym stanem TYLKO gdy filtr "tylko dostępne" jest włączony
                const isOutOfStock = !isService && stock <= 0 && currentOnlyAvailable;

                return (
                  <tr
                    key={product.id}
                    onClick={() => !isOutOfStock && handleProductSelect(product)}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      backgroundColor: isSelected ? 'var(--info-light)' : 'var(--card-bg)',
                      cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                      opacity: isOutOfStock ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isOutOfStock && !isSelected) {
                        e.target.style.backgroundColor = 'var(--bg)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isOutOfStock && !isSelected) {
                        e.target.style.backgroundColor = 'var(--card-bg)';
                      }
                    }}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{
                        fontWeight: '500',
                        fontSize: '11px',
                        color: isOutOfStock ? 'var(--text-muted)' : 'var(--text)',
                        marginBottom: '2px'
                      }}>
                        {product.name || product.nazwa}
                        {isSelected && (
                          <i className="fas fa-check text-success ms-1" style={{ fontSize: '10px' }}></i>
                        )}
                      </div>
                      {(product.barcode || product.kod_kreskowy) && (
                        <div style={{
                          fontSize: '9px',
                          color: 'var(--text-muted)',
                          fontFamily: 'monospace'
                        }}>
                          {product.barcode || product.kod_kreskowy}
                        </div>
                      )}
                      {(product.category || product.kategoria) && (
                        <span style={{
                          fontSize: '8px',
                          backgroundColor: 'var(--border)',
                          color: 'var(--text-secondary)',
                          padding: '1px 4px',
                          borderRadius: '2px',
                          marginTop: '2px',
                          display: 'inline-block'
                        }}>
                          {product.category || product.kategoria}
                        </span>
                      )}
                      {/* Oznaczenie usługi */}
                      {isService && (
                        <div style={{
                          fontSize: '8px',
                          border: '1px solid var(--purple)', color: 'var(--purple)' ,
                          padding: '1px 4px',
                          borderRadius: '2px',
                          marginTop: '2px',
                          display: 'inline-block',
                          fontWeight: '600'
                        }}>
                          USŁUGA
                        </div>
                      )}
                      {/* Oznaczenie ceny specjalnej */}
                      {product.has_special_price === 1 && (
                        <div style={{
                          fontSize: '8px',
                          backgroundColor: 'var(--warning)',
                          color: '#000',
                          padding: '1px 4px',
                          borderRadius: '2px',
                          marginTop: '2px',
                          display: 'inline-block',
                          fontWeight: '600'
                        }}>
                          CENA SPECJALNA
                        </div>
                      )}
                      {/* Oznaczenie produktu premiowanego */}
                      {(() => {
                        const bp = premiumProducts.find(p =>
                          p.product_id === (product.id || product.product_id) ||
                          (p.kategoria_id && p.kategoria_id === product.kategoria_id)
                        );
                        if (!bp) return null;
                        return (
                          <div style={{
                            fontSize: '8px',
                            border: '1px solid #f59e0b',
                            color: '#f59e0b',
                            padding: '1px 4px',
                            borderRadius: '2px',
                            marginTop: '2px',
                            display: 'inline-block',
                            fontWeight: '600'
                          }}>
                            ⭐ PREMIA +{Number(bp.kwota_za_sztuke || 0).toFixed(2)} zł
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {isService ? (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '500',
                          color: 'var(--purple)',
                          backgroundColor: 'var(--purple-light)',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          border: '1px solid var(--purple-light)'
                        }}>
                          —
                        </span>
                      ) : (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '500',
                          color: stock > 10 ? 'var(--success)' : stock > 0 ? 'var(--orange)' : 'var(--danger)',
                          backgroundColor: stock > 10 ? 'var(--success-light)' : stock > 0 ? 'var(--warning-light)' : 'var(--danger-light)',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          border: `1px solid ${stock > 10 ? 'var(--success-light)' : stock > 0 ? 'var(--warning-light)' : 'var(--danger-light)'}`
                        }}>
                          {stock} {product.unit || product.jednostka || 'szt'}
                        </span>
                      )}
                      {isOutOfStock && !isService && (
                        <div style={{
                          fontSize: '8px',
                          color: 'var(--danger)',
                          fontWeight: '600',
                          marginTop: '2px'
                        }}>
                          BRAK
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding: '6px 8px',
                      textAlign: 'right',
                      fontWeight: '600',
                      fontSize: '11px',
                      color: isOutOfStock ? 'var(--text-muted)' : (product.has_special_price ? 'var(--warning)' : 'var(--success)')
                    }}>
                      <div>
                        {price.toFixed(2)} zł
                        {product.has_special_price === 1 && (
                          <div style={{
                            fontSize: '8px',
                            color: 'var(--text-muted)',
                            fontWeight: '400',
                            textDecoration: 'line-through'
                          }}>
                            {parseFloat(product.default_price_brutto || 0).toFixed(2)} zł
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <button
                        data-pos-btn="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOutOfStock) {
                            handleProductSelect(product);
                          }
                        }}
                        disabled={isOutOfStock}
                        style={{
                          backgroundColor: isOutOfStock ? 'var(--text-muted)' : isSelected ? 'var(--success)' : 'var(--info)',
                          color: '#fff',
                          padding: '4px 10px',
                          fontSize: '11px'
                        }}
                        title={isOutOfStock ? 'Brak na stanie' : isSelected ? 'Już w koszyku' : 'Dodaj do koszyka'}
                      >
                        {isOutOfStock ? '✕' : isSelected ? '✓' : '+'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showResults && products.length === 0 && !loading && (
        <div className="text-center py-3 text-muted">
          <i className="fas fa-search fa-2x mb-2"></i>
          <div>Nie znaleziono produktów</div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ProductSearch);
